import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFirestore } from '../../contexts/FirestoreContext';
import { doc, getDoc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { Card, List, Tag, Empty, Spin, Typography, Space, Badge, Tooltip, Divider, Button, message, Row, Col, Collapse } from 'antd';
import { ClockCircleOutlined, AlertOutlined, FileTextOutlined, GlobalOutlined, PhoneOutlined, MailOutlined, EditOutlined, SaveOutlined, SettingOutlined, BookOutlined } from '@ant-design/icons';
import RegulatoryTriggerSelector from '../RegulatoryTriggerSelector';
import { getTriggerChanges, formatTimelineChangeSummary } from '../../utils/timelineManager';
import { createIncidentLog, getUserFullName, ACTIVITY_TYPES } from '../../utils/activityLogger';
import { syncRegulatoryDeadlines } from '../../utils/regulatoryDeadlines';
import { cleanupDuplicateRegulatoryDeadlines, getDuplicateRegulatoryDeadlinesReport } from '../../utils/cleanupDuplicateDeadlines';
import './RegulatoryComplianceTab.css';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const RegulatoryComplianceTab = ({ incidentId, isActive }) => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [regulatoryRules, setRegulatoryRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userOrgId, setUserOrgId] = useState(null);
  const [organisationName, setOrganisationName] = useState('');
  const [incident, setIncident] = useState(null);
  const [selectedRegulatoryTriggers, setSelectedRegulatoryTriggers] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeKeys, setActiveKeys] = useState(['active']); // Default to Active Issues expanded
  const [cleaningUp, setCleaningUp] = useState(false);
  const [duplicateReport, setDuplicateReport] = useState(null);

  // Permission check - ANY CMT member can edit regulatory triggers (same as milestones)
  const isCMTMember = (user) => {
    if (!user) return false;
    
    // Super admin/admin access
    if (user.isSuperAdmin || user.isAdmin) return true;
    
    // Developer access
    if (user.role === 'developer') return true;
    
    // ANY CMT role can edit regulatory triggers - aligned with milestone permissions
    const cmtRoles = [
      'cmt_leader', 'cmt_leader_alt', 'cmt_admin',
      'crisis_manager', 'safety_officer', 'comms_media',
      'legal_compliance', 'client_service', 'safety_security',
      'facilities', 'talent_hr', 'legal', 'it_tech', 'finance', 'other'
    ];
    
    if (user.role && cmtRoles.includes(user.role)) {
      return true;
    }
    
    // 🎯 MODERNIZED: Check via MultiCmtContext
    // If user has activeCmt, they have access
    return true; // Access controlled by MultiCmtContext membership validation
    
    return false;
  };
  
  const canEdit = isCMTMember(currentUser);

  // Helper function to map sender to CMT role
  const mapSenderToCmtRole = (sender) => {
    const senderLower = sender.toLowerCase();
    
    // Map common sender titles to CMT roles
    if (senderLower.includes('ciso') || senderLower.includes('security') || senderLower.includes('cyber')) {
      return 'safety_security';
    }
    if (senderLower.includes('ceo') || senderLower.includes('leader') || senderLower.includes('director')) {
      return 'cmt_leader';
    }
    if (senderLower.includes('legal') || senderLower.includes('counsel')) {
      return 'legal';
    }
    if (senderLower.includes('hr') || senderLower.includes('human') || senderLower.includes('people')) {
      return 'talent_hr';
    }
    if (senderLower.includes('it') || senderLower.includes('tech') || senderLower.includes('technology')) {
      return 'it_tech';
    }
    if (senderLower.includes('finance') || senderLower.includes('cfo') || senderLower.includes('financial')) {
      return 'finance';
    }
    if (senderLower.includes('comms') || senderLower.includes('communication') || senderLower.includes('media') || senderLower.includes('pr')) {
      return 'comms_media';
    }
    if (senderLower.includes('facilities') || senderLower.includes('operations')) {
      return 'facilities';
    }
    
    // Default to CMT Leader if no specific match
    return 'cmt_leader';
  };

  // Note: Task creation function removed per user request
  // Only regulatory deadlines are managed automatically

  // Cleanup duplicate regulatory deadlines
  const handleCleanupDuplicates = async () => {
    if (!userOrgId || !incidentId) {
      message.error('Missing required data for cleanup');
      return;
    }

    try {
      setCleaningUp(true);
      
      // First get a report of duplicates
      const report = await getDuplicateRegulatoryDeadlinesReport(incidentId, userOrgId, db);
      console.log('📊 Duplicate report:', report);
      
      if (report.duplicateGroups === 0) {
        message.info('No duplicate regulatory deadlines found');
        return;
      }
      
      // Show confirmation with details
      const confirmed = window.confirm(
        `Found ${report.totalDeadlines} regulatory deadlines with ${report.duplicateGroups} duplicate groups.\n\n` +
        `This will delete ${report.totalDeadlines - report.uniqueCombinations} duplicate records.\n\n` +
        `Continue with cleanup?`
      );
      
      if (!confirmed) return;
      
      // Perform cleanup
      const result = await cleanupDuplicateRegulatoryDeadlines(incidentId, userOrgId, db);
      
      message.success(
        `Cleanup completed! Deleted ${result.duplicatesDeleted} duplicates. ` +
        `${result.uniqueRemaining} unique deadlines remaining.`
      );
      
      // Refresh the page to see updated timeline
      window.location.reload();
      
    } catch (error) {
      console.error('❌ Error during cleanup:', error);
      message.error('Failed to cleanup duplicates: ' + error.message);
    } finally {
      setCleaningUp(false);
    }
  };

  // Get duplicate report without cleanup
  const handleGetDuplicateReport = async () => {
    if (!userOrgId || !incidentId) return;
    
    try {
      const report = await getDuplicateRegulatoryDeadlinesReport(incidentId, userOrgId, db);
      setDuplicateReport(report);
      console.log('📊 Duplicate report:', report);
    } catch (error) {
      console.error('❌ Error getting duplicate report:', error);
    }
  };

  // Auto-check for duplicates when component loads
  useEffect(() => {
    if (userOrgId && incidentId && isActive) {
      handleGetDuplicateReport();
    }
  }, [userOrgId, incidentId, isActive]);

  // Helper function to convert time units to milliseconds
  const getTimeMultiplier = (unit) => {
    switch (unit.toLowerCase()) {
      case 'immediate':
        return 15 * 60 * 1000; // 15 minutes for immediate notifications
      case 'minutes':
        return 60 * 1000;
      case 'hours':
        return 60 * 60 * 1000;
      case 'days':
        return 24 * 60 * 60 * 1000;
      case 'weeks':
        return 7 * 24 * 60 * 60 * 1000;
      default:
        return 60 * 60 * 1000; // Default to hours
    }
  };

  // Function to fetch fresh data
  const fetchData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const userDocRef = doc(db, 'c3_users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const orgId = userDoc.data().orgId;
        setUserOrgId(orgId);
        
        // Fetch organization's regulatory rules with cache busting if needed
        const orgDocRef = doc(db, 'c3_organisations', orgId);
        
        // Force fresh data from server if requested
        const orgDoc = forceRefresh ? 
          await getDoc(orgDocRef, { source: 'server' }) : 
          await getDoc(orgDocRef);
          
        if (orgDoc.exists()) {
          const orgData = orgDoc.data();
          console.log('Fetched regulatory rules (force refresh:', forceRefresh, '):', orgData.regulatoryRules);
          setRegulatoryRules(orgData.regulatoryRules || []);
          setOrganisationName(orgData.name || 'Organisation');
        }

        // Fetch incident data if incidentId is provided
        if (incidentId) {
          const incidentDocRef = doc(db, 'c3_incidents', incidentId);
          const incidentDoc = await getDoc(incidentDocRef);
          if (incidentDoc.exists()) {
            const incidentData = { id: incidentDoc.id, ...incidentDoc.data() };
            setIncident(incidentData);
            setSelectedRegulatoryTriggers(incidentData.selectedRegulatoryTriggers || []);
          }
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load regulatory compliance data');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentUser.uid, db, incidentId]);

  // Auto-refresh when tab becomes active
  useEffect(() => {
    if (isActive) {
      console.log('RegulatoryComplianceTab became active, fetching fresh data...');
      fetchData(true);
    }
  }, [isActive]); // Refresh whenever the tab becomes active



  const getMethodIcon = (method) => {
    if (typeof method === 'string') {
      method = method.toLowerCase();
      if (method.includes('phone') || method.includes('call')) return <PhoneOutlined />;
      if (method.includes('email') || method.includes('mail')) return <MailOutlined />;
      if (method.includes('online') || method.includes('portal')) return <GlobalOutlined />;
    }
    return <FileTextOutlined />;
  };

  const getMethodColor = (method) => {
    if (typeof method === 'string') {
      method = method.toLowerCase();
      if (method.includes('phone') || method.includes('call')) return 'blue';
      if (method.includes('email') || method.includes('mail')) return 'green';
      if (method.includes('online') || method.includes('portal')) return 'purple';
    }
    return 'default';
  };

  const formatDeadline = (rule) => {
    // Handle immediate notifications
    if (rule.immediateNotificationRequired || rule.deadlineUnit === 'immediate') {
      return 'Immediate';
    }
    
    // Handle both old format (just deadline number) and new format (deadlineValue + deadlineUnit)
    if (rule.deadlineValue && rule.deadlineUnit) {
      return `${rule.deadlineValue} ${rule.deadlineUnit}`;
    }
    
    // Fallback to old format
    const deadline = rule.deadline || rule.deadlineValue;
    if (typeof deadline === 'number') {
      if (deadline < 60) return `${deadline} minutes`;
      if (deadline < 1440) return `${Math.floor(deadline / 60)} hours`;
      return `${Math.floor(deadline / 1440)} days`;
    }
    return deadline;
  };

  const getUrgencyLevel = (rule) => {
    // Immediate notifications are always critical
    if (rule.immediateNotificationRequired || rule.deadlineUnit === 'immediate') {
      return { level: 'immediate', color: '#ff0000' }; // Bright red for immediate
    }
    
    // Convert deadline to minutes for comparison
    let deadlineInMinutes = 0;
    
    if (rule.deadlineValue && rule.deadlineUnit) {
      const value = parseInt(rule.deadlineValue);
      const unit = rule.deadlineUnit.toLowerCase();
      
      if (unit.includes('minute')) deadlineInMinutes = value;
      else if (unit.includes('hour')) deadlineInMinutes = value * 60;
      else if (unit.includes('day')) deadlineInMinutes = value * 1440;
      else if (unit.includes('week')) deadlineInMinutes = value * 10080;
    } else {
      // Fallback to old format
      deadlineInMinutes = rule.deadline || 0;
    }
    
    if (deadlineInMinutes <= 120) return { level: 'critical', color: 'red' }; // 2 hours or less
    if (deadlineInMinutes <= 360) return { level: 'urgent', color: 'orange' }; // 6 hours or less
    if (deadlineInMinutes <= 1440) return { level: 'warning', color: 'gold' }; // 24 hours or less
    return { level: 'normal', color: 'blue' };
  };

  const handleSaveRegulatoryTriggers = async () => {
    if (!incident?.id || !canEdit) {
      message.error('You need to be a CMT member to edit regulatory triggers');
      return;
    }
    
    setSaving(true);
    try {
      const previousTriggers = incident.selectedRegulatoryTriggers || [];
      const changes = getTriggerChanges(previousTriggers, selectedRegulatoryTriggers);
      
      // Update incident with new triggers
      const incidentRef = doc(db, 'c3_incidents', incident.id);
      await updateDoc(incidentRef, {
        selectedRegulatoryTriggers: selectedRegulatoryTriggers,
        updatedAt: new Date()
      });
      
      // Use universal regulatory system to create BOTH deadlines AND tasks
      try {
        const syncResults = await syncRegulatoryDeadlines(
          incident.id,
          userOrgId,
          incident.occurredAt,
          regulatoryRules,
          incident.crisisType || incident.type,
          selectedRegulatoryTriggers,
          currentUser.uid,
          db
        );
        
        console.log('🎯 Universal regulatory sync from compliance tab:', syncResults);
        
        // Note: Task creation tracking removed per user request
        // Only regulatory deadlines are managed automatically
        
      } catch (syncError) {
        console.error('❌ Error with regulatory sync:', syncError);
        message.error('Failed to sync regulatory deadlines');
      }
      
      // Log regulatory triggers update
      try {
        const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
        await createIncidentLog({
          incidentId: incident.id,
          orgId: userOrgId,
          action: ACTIVITY_TYPES.REGULATORY_TRIGGERS_UPDATED,
          details: `Regulatory triggers updated: ${changes.hasChanges ? formatTimelineChangeSummary(changes, regulatoryRules) : 'No changes made'}`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName,
          priority: changes.addedTriggers.length > 0 ? 'high' : 'normal',
          metadata: {
            addedTriggers: changes.addedTriggers,
            removedTriggers: changes.removedTriggers,
            totalTriggers: selectedRegulatoryTriggers.length,
            changeType: 'regulatory_triggers'
          }
        });
      } catch (logError) {
        console.error('Failed to log regulatory triggers update:', logError);
      }
      
      // Update local incident state
      setIncident(prev => ({
        ...prev,
        selectedRegulatoryTriggers: selectedRegulatoryTriggers
      }));
      
      setIsEditing(false);
      
      // Trigger a timeline refresh by dispatching a custom event
      window.dispatchEvent(new CustomEvent('regulatoryTriggersUpdated', {
        detail: { incidentId: incident.id, changes }
      }));
      
              // Provide feedback about regulatory deadline changes only
        if (changes.hasChanges) {
          const changeSummary = formatTimelineChangeSummary(changes, regulatoryRules);
          message.success(`Regulatory triggers updated successfully. ${changeSummary}. Timeline will refresh automatically.`);
        } else {
          message.success('Regulatory triggers updated successfully.');
        }
    } catch (error) {
      console.error('Error updating regulatory triggers:', error);
      message.error('Failed to update regulatory triggers');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset to original values
    setSelectedRegulatoryTriggers(incident?.selectedRegulatoryTriggers || []);
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Loading regulatory compliance data...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (regulatoryRules.length === 0) {
    return (
      <div className="regulatory-compliance-container">
        <Card>
          <Empty
            description={
              <div>
                <Text strong>No regulatory triggers configured</Text>
                <br />
                <Text type="secondary">
                  Contact your administrator to set up regulatory compliance triggers for your organization.
                </Text>
              </div>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="regulatory-compliance-container">
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <Title level={4} style={{ margin: 0, color: '#39325b' }}>
            <AlertOutlined style={{ marginRight: '8px' }} />
            Regulatory Compliance
          </Title>
          {duplicateReport && duplicateReport.duplicateGroups > 0 && (
            <Tooltip title={`Found ${duplicateReport.totalDeadlines} regulatory deadlines with ${duplicateReport.duplicateGroups} duplicate groups. Click to clean up duplicates.`}>
              <Button
                type="primary"
                danger
                size="small"
                loading={cleaningUp}
                onClick={handleCleanupDuplicates}
                style={{ fontSize: '11px' }}
              >
                🧹 Clean Up {duplicateReport.totalDeadlines - duplicateReport.uniqueCombinations} Duplicates
              </Button>
            </Tooltip>
          )}
        </div>
        <Text type="secondary">
          Regulatory requirements and deadlines for <strong>{organisationName}</strong>
        </Text>
        {duplicateReport && duplicateReport.duplicateGroups > 0 && (
          <div style={{ marginTop: '8px' }}>
            <Text type="warning" style={{ fontSize: '12px' }}>
              ⚠️ Found {duplicateReport.duplicateGroups} duplicate regulatory deadline groups. Use the cleanup button above to fix timeline display issues.
            </Text>
          </div>
        )}
      </div>

      {/* Expandable Sections */}
      <Collapse 
        activeKey={activeKeys} 
        onChange={setActiveKeys}
        ghost
        size="large"
      >
        {/* Active Regulatory Compliance Issues - Default Expanded */}
        {incident && selectedRegulatoryTriggers.length > 0 && (
          <Panel 
            header={
              <Space>
                <AlertOutlined style={{ fontSize: '18px', color: '#ff4d4f' }} />
                <strong style={{ fontSize: '16px', color: '#ff4d4f' }}>
                  🚨 Active Regulatory Compliance Issues
                </strong>
                <Badge 
                  count={selectedRegulatoryTriggers.length} 
                  style={{ backgroundColor: '#ff4d4f' }}
                />
              </Space>
            } 
            key="active"
          >
            <Card 
              style={{ 
                backgroundColor: '#fff2f0',
                border: '1px solid #ff4d4f',
                borderLeft: '4px solid #ff4d4f'
              }}
            >
              <Text style={{ color: '#666', marginBottom: '12px', display: 'block' }}>
                The following regulatory requirements have been triggered for this incident and require immediate attention:
              </Text>
              <Space direction="vertical" style={{ width: '100%' }}>
                {selectedRegulatoryTriggers.map((trigger, index) => {
                  const rule = regulatoryRules.find(r => r.trigger === trigger);
                  if (!rule) return null;
                  
                  return (
                    <Card 
                      key={index}
                      size="small"
                      style={{ 
                        backgroundColor: '#fff',
                        border: '1px solid #ff4d4f',
                        borderLeft: '3px solid #ff4d4f'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <Text strong style={{ color: '#ff4d4f', fontSize: '14px' }}>
                            {rule.regulator}
                          </Text>
                          <br />
                          <Text style={{ fontSize: '12px', color: '#666' }}>
                            {rule.trigger}
                          </Text>
                          <br />
                          <Space size="small" style={{ marginTop: '4px' }}>
                            <Tag color="red" size="small">
                              Deadline: {formatDeadline(rule)}
                            </Tag>
                            <Tag color="red" size="small">
                              Method: {Array.isArray(rule.method) ? rule.method.join(', ') : rule.method}
                            </Tag>
                            <Tag color="red" size="small">
                              Sender: {rule.sender}
                            </Tag>
                          </Space>
                        </div>
                        {rule.immediateNotificationRequired && (
                          <Tag color="red" icon={<AlertOutlined />} style={{ fontWeight: 'bold', fontSize: '10px' }}>
                            IMMEDIATE
                          </Tag>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </Space>
            </Card>
          </Panel>
        )}

        {/* Manage Regulatory Triggers Section */}
        {incident && (
          <Panel 
            header={
              <Space>
                <SettingOutlined style={{ fontSize: '18px', color: '#39325b' }} />
                <strong style={{ fontSize: '16px' }}>📋 Manage Regulatory Triggers</strong>
                {!isEditing && canEdit && (
                  <Badge count="Edit" style={{ backgroundColor: '#39325b' }} />
                )}
                {isEditing && (
                  <Badge count="Editing" style={{ backgroundColor: '#faad14' }} />
                )}
              </Space>
            } 
            key="manage"
            extra={
              <div onClick={(e) => e.stopPropagation()}>
                {!isEditing && canEdit && (
                  <Tooltip title="Edit regulatory triggers: Modify which regulatory requirements apply to this incident. Changes will update regulatory deadlines on the timeline automatically.">
                    <Button
                      type="primary"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => setIsEditing(true)}
                      style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
                    >
                      Edit
                    </Button>
                  </Tooltip>
                )}
                {isEditing && canEdit && (
                  <Space>
                    <Tooltip title="Save changes: Apply the selected regulatory triggers to this incident. This will create new regulatory deadlines on the timeline and log the changes.">
                      <Button
                        type="primary"
                        size="small"
                        icon={<SaveOutlined />}
                        onClick={handleSaveRegulatoryTriggers}
                        loading={saving}
                        style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
                      >
                        Save
                      </Button>
                    </Tooltip>
                    <Tooltip title="Cancel editing: Discard any changes and return to view mode without saving.">
                      <Button size="small" onClick={handleCancelEdit}>
                        Cancel
                      </Button>
                    </Tooltip>
                  </Space>
                )}
              </div>
            }
          >
            <Card 
              style={{ 
                backgroundColor: '#fafafa',
                border: '1px solid #d9d9d9'
              }}
            >
              <Text type="secondary" style={{ fontSize: '12px', marginBottom: '16px', display: 'block' }}>
                Select which regulatory requirements apply to this incident
              </Text>
              
              <RegulatoryTriggerSelector
                regulatoryRules={regulatoryRules}
                incidentType={incident.crisisType || ''}
                selectedTriggers={selectedRegulatoryTriggers}
                onChange={setSelectedRegulatoryTriggers}
                disabled={!isEditing || !canEdit}
                showAutoSuggestions={false}
              />
              
              {incident && !canEdit && (
                <div style={{ marginTop: '12px', fontSize: '12px', color: '#999' }}>
                  <em>Note: You need to be a CMT member to edit regulatory triggers</em>
                </div>
              )}
            </Card>
          </Panel>
        )}

        {/* Available Regulatory Requirements Reference */}
        <Panel 
          header={
            <Space>
              <BookOutlined style={{ fontSize: '18px', color: '#39325b' }} />
              <strong style={{ fontSize: '16px' }}>📚 Available Regulatory Requirements Reference</strong>
              <Badge count={regulatoryRules.length} style={{ backgroundColor: '#52c41a' }} />
            </Space>
          } 
          key="reference"
        >
          <div style={{ marginBottom: '16px' }}>
            <Text type="secondary">
              Complete list of regulatory requirements configured for your organization (for reference)
            </Text>
          </div>

          <Row gutter={[16, 16]}>
            {regulatoryRules.map((rule, index) => {
              const isTriggered = selectedRegulatoryTriggers.includes(rule.trigger);
              
              return (
                <Col xs={24} sm={12} md={8} lg={6} key={index}>
                  <Card
                    size="small"
                    style={{
                      height: '100%',
                      backgroundColor: '#fff',
                      border: '1px solid #e8e8e8',
                      borderRadius: '6px'
                    }}
                    styles={{ body: { padding: '12px' } }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <Text strong style={{ fontSize: '13px', color: '#39325b', lineHeight: '1.3' }}>
                        {rule.regulator}
                      </Text>
                      {rule.immediateNotificationRequired && (
                        <Tag 
                          color="orange" 
                          size="small"
                          style={{ 
                            fontSize: '9px', 
                            fontWeight: 'bold',
                            marginLeft: '4px',
                            lineHeight: '1.2'
                          }}
                        >
                          IMMEDIATE
                        </Tag>
                      )}
                    </div>
                    
                    <Text style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '8px', lineHeight: '1.4' }}>
                      {rule.trigger}
                    </Text>
                    
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                        <Tag size="small" color="blue" style={{ fontSize: '9px', margin: '1px' }}>
                          {formatDeadline(rule)}
                        </Tag>
                        <Tag size="small" color="green" style={{ fontSize: '9px', margin: '1px' }}>
                          {Array.isArray(rule.method) ? rule.method.join(', ') : rule.method}
                        </Tag>
                        <Tag size="small" color="purple" style={{ fontSize: '9px', margin: '1px' }}>
                          {rule.sender}
                        </Tag>
                      </div>
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>

          <Divider />

          <Card 
            size="small" 
            className="info-card"
            style={{ 
              marginTop: '16px'
            }}
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong style={{ color: '#39325b' }}>
                <AlertOutlined style={{ marginRight: '8px' }} />
                Important Notes:
              </Text>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>
                  <Text type="secondary">
                    These triggers are automatically monitored when incidents occur
                  </Text>
                </li>
                <li>
                  <Text type="secondary">
                    Regulatory deadlines will appear on the Timeline when an incident occurrence time is set
                  </Text>
                </li>
                <li>
                  <Text type="secondary">
                    Contact your administrator to modify these regulatory requirements
                  </Text>
                </li>
              </ul>
            </Space>
          </Card>
        </Panel>
      </Collapse>
    </div>
  );
};

export default RegulatoryComplianceTab; 